import { useEffect, useRef } from "react";

import { spacing } from "#app/theme/v1/spacing";
import { PartDto } from "#modules/parts/infra/fetchPart.api";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { DetailsPanelContent } from "#modules/quoteRequestLines/views/DetailsPanel/DetailsPanelContent";
import { ProcessStepForTable } from "#modules/quoteRequestLines/views/DetailsPanel/ProcessSteps/ProcessStepsTable";
import { useAnalytics, AnalyticsEvent } from "#shared/useAnalytics";
import { Drawer } from "#shared/view/components/Drawer";
import { AssemblyWithChildren } from "shared";

type Props = {
  onClosePanel: () => void;
  sidePanelOpen: boolean;
  part: PartDto | undefined | null;
  quoteRequestLine: QuoteRequestLineDto | undefined;
  assembliesInBatch: AssemblyWithChildren<QuoteRequestLineDto>[];
  assemblyChildren: AssemblyWithChildren<QuoteRequestLineDto>[];
  onAssemblyActivatedForBatch?: (quoteRequestLineId: string) => void;
  hasChildren: boolean;
  isChild: boolean;
  processSteps: ProcessStepForTable[];
};

export const DetailsPanel = ({
  onClosePanel,
  sidePanelOpen,
  part,
  quoteRequestLine,
  assembliesInBatch,
  assemblyChildren,
  onAssemblyActivatedForBatch,
  hasChildren,
  isChild,
  processSteps,
}: Props) => {
  const analytics = useAnalytics();
  const openedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (sidePanelOpen) {
      openedAtRef.current = Date.now();
      analytics.capture(AnalyticsEvent.DetailsPanelOpened, {
        quote_request_line_id: quoteRequestLine?.id,
        is_batch: assembliesInBatch.length > 1,
      });
    } else if (openedAtRef.current !== null) {
      analytics.capture(AnalyticsEvent.DetailsPanelClosed, {
        quote_request_line_id: quoteRequestLine?.id,
        time_spent_ms: Date.now() - openedAtRef.current,
      });
      openedAtRef.current = null;
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [sidePanelOpen]);

  if (!quoteRequestLine || !part) {
    return null;
  }

  return (
    <Drawer.Root
      opened={sidePanelOpen}
      offset={spacing.xxs}
      radius={"xs"}
      closeOnEscape={false}
      position="right"
      onClose={onClosePanel}
      size={"xl"}
      onClick={(e) => e.stopPropagation()}
    >
      <Drawer.Overlay backgroundOpacity={0.2} />
      {sidePanelOpen && (
        <DetailsPanelContent
          part={part}
          quoteRequestLine={quoteRequestLine}
          assembliesInBatch={assembliesInBatch}
          assemblyChildren={assemblyChildren}
          onAssemblyActivatedForBatch={onAssemblyActivatedForBatch}
          hasChildren={hasChildren}
          isChild={isChild}
          processSteps={processSteps}
          onClosePanel={onClosePanel}
        />
      )}
    </Drawer.Root>
  );
};
